import { ROUTES } from "@app/constants";
import {
  BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY,
  BRIEF_LIST_QUERY_KEY,
} from "@entities/brief/lib/constants";
import usePollNotification from "@entities/brief/lib/hooks/notifications/usePollNotification";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import {
  BriefNotificationActionType,
  BriefNotificationStatusType,
  IBriefNotification,
} from "@shared/lib/types/brief";
import { Button } from "@shared/ui";

import {
  BRIEF_CAMPAIGN_PENDING_REVIEW_QUERY_KEY,
  BRIEF_CAMPAIGNS_LIST_QUERY_KEY,
} from "@entities/campaigns/lib/contsants";
import { useQueryClient } from "@tanstack/react-query";
import { Loader, RefreshCw } from "lucide-react";
import { useState } from "react";
import {
  getNotificationActionDescription,
  getNotificationActionTitle,
  getNotificationStatusLabel,
} from "../lib/constants";
import styles from "../styles.module.scss";
import { NotificationResultsModal } from "./NotificationResultsModal";

interface NotificationContentProps {
  notification: IBriefNotification;
  briefId: string;
  onClose: (notificationId: string) => void;
}

export function NotificationContent({
  notification,
  briefId,
  onClose,
}: NotificationContentProps) {
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { userDetails } = useGetMe();

  const { notification: polledNotification, isSuccess } = usePollNotification(
    briefId,
    notification._id
  );

  const displayNotification = polledNotification ?? notification;
  const statusLabel = getNotificationStatusLabel(displayNotification.status);

  const isSameUser =
    userDetails?.userEntity?.id === displayNotification.createdBy?._id;
  const actionTitle = getNotificationActionTitle(
    displayNotification.actionType
  );
  const actionDescription = getNotificationActionDescription(
    displayNotification.actionType
  );

  const hasResults =
    Array.isArray(displayNotification.result) &&
    displayNotification.result.length > 0;

  const isCompleted =
    displayNotification.status === BriefNotificationStatusType.Completed;

  const isProcessing =
    displayNotification.status === BriefNotificationStatusType.Processing ||
    displayNotification.status === BriefNotificationStatusType.Pending;

  const handleReload = () => {
    queryClient.invalidateQueries({
      queryKey: [...BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [...BRIEF_LIST_QUERY_KEY],
    });

    queryClient.invalidateQueries({
      queryKey: [...BRIEF_CAMPAIGNS_LIST_QUERY_KEY(briefId)],
    });
    queryClient.invalidateQueries({
      queryKey: [...BRIEF_CAMPAIGNS_LIST_QUERY_KEY(briefId)],
    });
    queryClient.invalidateQueries({
      queryKey: [...BRIEF_CAMPAIGN_PENDING_REVIEW_QUERY_KEY(briefId)],
    });
    onClose(notification._id);
  };

  const handleOkayResults = () => {
    setResultsModalOpen(false);

    handleReload();
  };

  const iconBgClass = isProcessing
    ? styles.cardIconProcessing
    : displayNotification.status === BriefNotificationStatusType.Failed
      ? styles.cardIconError
      : styles.cardIconDefault;

  return (
    <>
      <div className={styles.cardContent}>
        <div className={`${styles.cardIcon} ${iconBgClass}`}>
          {isProcessing ? (
            <Loader size={16} className={styles.loaderSpin} />
          ) : (
            <span className={styles.cardIconDot} />
          )}
        </div>
        <div className={styles.cardBody}>
          <div className={styles.cardTitle}>{actionTitle}</div>
          <p className={styles.cardMessage}>
            {[actionDescription, statusLabel].filter(Boolean).join(" — ")}
            {displayNotification?.actionType ===
              BriefNotificationActionType.copyToAnotherBrief &&
              (isCompleted ||
                displayNotification.status ===
                  BriefNotificationStatusType.Failed) &&
              displayNotification?.actionParams?.copyToBriefId && (
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => {
                    onClose(notification._id);
                    window.open(
                      ROUTES.BRIEF_V2.replace(
                        ":briefId",
                        displayNotification?.actionParams?.copyToBriefId || ""
                      ),
                      "_blank"
                    );
                  }}
                >
                  Brief Link
                </Button>
              )}
          </p>
          {displayNotification.createdBy && isCompleted && (
            <>
              {!isSameUser ? (
                <p className={styles.cardCreatedBy}>
                  Another user ({displayNotification.createdBy.firstName}{" "}
                  {displayNotification.createdBy.lastName}) started this action.
                  Refresh to see the latest brief data.
                </p>
              ) : (
                <p className={styles.cardCreatedBy}>
                  Refresh to see the latest brief data.
                </p>
              )}
            </>
          )}

          {(hasResults || isCompleted) && (
            <div className={styles.cardActions}>
              {hasResults && isSuccess && (
                <Button
                  onClick={() => setResultsModalOpen(true)}
                  size="small"
                  type="primary"
                >
                  See results
                </Button>
              )}
              {isCompleted && (
                <Button
                  onClick={handleReload}
                  size="small"
                  icon={<RefreshCw size={14} />}
                >
                  Refresh
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <NotificationResultsModal
        open={resultsModalOpen}
        title={actionTitle}
        result={displayNotification.result}
        actionType={displayNotification.actionType}
        onOkay={handleOkayResults}
      />
    </>
  );
}
